import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit3, Trash2, RefreshCw, ConciergeBell } from 'lucide-react';
import { backofficeApi, formatNumber, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { AmenityDto, AmenityCreateRequestDto, AmenityUpdateRequestDto } from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  SearchInput,
  Select,
  TextInput,
  Switch,
  Modal,
  Pagination,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import { useDebouncedValue } from '../utils/useDebouncedValue';
import DataState from '../components/DataState';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  strong {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const CodeText = styled.code`
  font-size: 0.76rem;
  color: ${(p) => p.theme.textMuted};
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const FieldHint = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: ${(p) => p.theme.textMuted};
`;

const ToggleField = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.85rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
  cursor: pointer;
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const STATUS_FILTER = [
  { value: '', label: 'Todas as situações' },
  { value: 'active', label: 'Ativas' },
  { value: 'inactive', label: 'Inativas' },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

interface AmenityForm {
  code: string;
  name: string;
  icon: string;
  sortOrder: string;
  active: boolean;
}

const EMPTY_FORM: AmenityForm = { code: '', name: '', icon: '', sortOrder: '0', active: true };

/**
 * O código é o que amarra a marcação já feita pelas lojas ao vocabulário, então
 * ele é normalizado na digitação: o backend guarda em MAIÚSCULAS, e deixar a
 * tela mostrar "wifi" enquanto o banco guarda "WIFI" faria a operação achar que
 * criou dois.
 */
const normalizeCode = (value: string): string =>
  value
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9_]/g, '_')
    .replace(/_{2,}/g, '_');

/**
 * Catálogo de comodidades (card 305).
 *
 * Fica em Plataforma, ao lado de Categorias e Localidades, porque é catálogo e
 * não fila de trabalho diária. O que se cria aqui aparece no perfil do lojista
 * e nos filtros da vitrine na próxima abertura da tela - sem deploy, que é o
 * critério de aceite que o card #205 nunca conseguiu passar.
 */
const AmenitiesPage: React.FC = () => {
  const { showNotification } = useNotification();
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [editing, setEditing] = useState<AmenityDto | null>(null);
  const [form, setForm] = useState<AmenityForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<AmenityDto | null>(null);
  const [removing, setRemoving] = useState<boolean>(false);

  const debouncedSearch = useDebouncedValue(search.trim());

  const { data, loading, error, reload } = useAsync(
    () =>
      backofficeApi.listAmenities({
        page,
        size: pageSize,
        query: debouncedSearch || undefined,
        active: statusFilter === '' ? undefined : statusFilter === 'active',
        sort: 'sortOrder,asc',
      }),
    [page, pageSize, debouncedSearch, statusFilter]
  );

  const amenities = data?.content ?? [];
  const totalItems = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const resetToFirstPage = (): void => setPage(0);

  const openCreate = (): void => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setIsOpen(true);
  };

  const openEdit = (amenity: AmenityDto): void => {
    setEditing(amenity);
    setForm({
      code: amenity.code,
      name: amenity.name,
      icon: amenity.icon ?? '',
      sortOrder: String(amenity.sortOrder),
      active: amenity.active,
    });
    setIsOpen(true);
  };

  const update = <K extends keyof AmenityForm>(key: K, value: AmenityForm[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (): Promise<void> => {
    if (!form.name.trim()) {
      showNotification('Informe o nome da comodidade.', 'error');
      return;
    }
    if (!editing && !form.code.trim()) {
      showNotification('Informe o código da comodidade.', 'error');
      return;
    }
    const sortOrder = Number(form.sortOrder);
    if (!Number.isFinite(sortOrder) || sortOrder < 0) {
      showNotification('A ordem de exibição deve ser um número igual ou maior que zero.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const icon = form.icon.trim() || null;
      if (editing) {
        const payload: AmenityUpdateRequestDto = { name: form.name.trim(), icon, sortOrder, active: form.active };
        await backofficeApi.updateAmenity(editing.id, payload);
        showNotification('Comodidade atualizada com sucesso.', 'success');
      } else {
        const payload: AmenityCreateRequestDto = {
          code: normalizeCode(form.code),
          name: form.name.trim(),
          icon,
          sortOrder,
          active: form.active,
        };
        await backofficeApi.createAmenity(payload);
        showNotification('Comodidade criada. Ela já aparece para seleção no painel do lojista.', 'success');
      }
      setIsOpen(false);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar a comodidade.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (amenity: AmenityDto): Promise<void> => {
    try {
      await backofficeApi.updateAmenity(amenity.id, {
        name: amenity.name,
        icon: amenity.icon,
        sortOrder: amenity.sortOrder,
        active: !amenity.active,
      });
      showNotification(
        amenity.active
          ? 'Comodidade desativada. As lojas que já a marcaram continuam marcadas.'
          : 'Comodidade ativada.',
        'success'
      );
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível alterar a situação.'), 'error');
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (!deleting) return;
    setRemoving(true);
    try {
      await backofficeApi.deleteAmenity(deleting.id);
      showNotification('Comodidade excluída.', 'success');
      setDeleting(null);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível excluir a comodidade.'), 'error');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Comodidades"
        subtitle="O que o visitante encontra no lugar: Wi-Fi, estacionamento, pet friendly. As ativas aparecem para o lojista marcar no perfil e viram filtro na vitrine."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} /> Nova comodidade
          </Button>
        }
      />

      <StatsGrid>
        <StatCard label="Total (respeita o filtro)" value={formatNumber(totalItems)} />
        <StatCard label="Ativas (nesta página)" value={formatNumber(amenities.filter((a) => a.active).length)} />
        <StatCard label="Inativas (nesta página)" value={formatNumber(amenities.filter((a) => !a.active).length)} />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por nome ou código..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetToFirstPage();
          }}
        />
        <Select
          options={STATUS_FILTER}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            resetToFirstPage();
          }}
        />
        <ActionButton title="Atualizar" onClick={reload}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        isEmpty={amenities.length === 0}
        emptyTitle="Nenhuma comodidade encontrada"
        emptyDescription="Cadastre a primeira comodidade para o lojista poder marcá-la no perfil da loja."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Comodidade</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ordem</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Lojas</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Situação</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {amenities.map((amenity) => (
                <tr key={amenity.id}>
                  <td>
                    <NameCell>
                      <ConciergeBell size={14} />
                      <strong>{amenity.name}</strong>
                      <CodeText>{amenity.code}</CodeText>
                    </NameCell>
                  </td>
                  <td style={{ textAlign: 'right' }}>{formatNumber(amenity.sortOrder)}</td>
                  <td style={{ textAlign: 'right' }}>{formatNumber(amenity.merchantCount)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Badge tone={amenity.active ? 'success' : 'secondary'}>
                      {amenity.active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </td>
                  <td>
                    <RowActions>
                      <Switch
                        checked={amenity.active}
                        onChange={() => toggleActive(amenity)}
                        aria-label={amenity.active ? 'Desativar comodidade' : 'Ativar comodidade'}
                      />
                      <ActionButton aria-label="Editar comodidade" title="Editar" onClick={() => openEdit(amenity)}>
                        <Edit3 size={16} />
                      </ActionButton>
                      <ActionButton
                        aria-label="Excluir comodidade"
                        title={
                          amenity.merchantCount > 0
                            ? 'Marcada por lojas - desative em vez de excluir'
                            : 'Excluir'
                        }
                        color="#DC2626"
                        onClick={() => setDeleting(amenity)}
                      >
                        <Trash2 size={16} />
                      </ActionButton>
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {totalPages > 1 && (
        <Pagination
          currentPage={page + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disabled={loading}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            resetToFirstPage();
          }}
        />
      )}

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editing ? 'Editar comodidade' : 'Nova comodidade'}
        width="460px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={submit} isLoading={submitting}>
              Salvar
            </Button>
          </>
        }
      >
        <TextInput
          label="Nome"
          value={form.name}
          onChange={(e) => update('name', e.target.value)}
          placeholder="Ex.: Drive-thru"
        />
        <TextInput
          label="Código"
          value={form.code}
          onChange={(e) => update('code', normalizeCode(e.target.value))}
          placeholder="Ex.: DRIVE_THRU"
          disabled={editing !== null}
          containerStyle={{ marginTop: '0.75rem' }}
        />
        <FieldHint>
          {editing
            ? 'O código não muda depois de criado: é ele que amarra a marcação já feita pelas lojas. Para corrigir o texto que aparece, use o nome.'
            : 'Identificador estável usado pelo site e pelo app. Depois de criado ele não pode mudar.'}
        </FieldHint>
        <FieldRow>
          <TextInput
            label="Ícone"
            value={form.icon}
            onChange={(e) => update('icon', e.target.value)}
            placeholder="Ex.: wifi"
          />
          <TextInput
            label="Ordem de exibição"
            type="number"
            min={0}
            value={form.sortOrder}
            onChange={(e) => update('sortOrder', e.target.value)}
          />
        </FieldRow>
        <FieldHint>
          A ordem define a sequência das etiquetas na página da loja e dos filtros da vitrine -
          menor primeiro.
        </FieldHint>

        <ToggleField>
          <Switch checked={form.active} onChange={(v) => update('active', v)} />
          Comodidade ativa (oferecida ao lojista e usada como filtro)
        </ToggleField>
        <FieldHint>
          Desativar tira a comodidade das telas, mas <strong>não</strong> desmarca as lojas que já a
          escolheram: reativá-la devolve a etiqueta a todas elas.
        </FieldHint>
      </Modal>

      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Excluir comodidade"
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)} disabled={removing}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmRemove} isLoading={removing} disabled={(deleting?.merchantCount ?? 0) > 0}>
              Excluir
            </Button>
          </>
        }
      >
        {deleting && deleting.merchantCount > 0 ? (
          <ConfirmText>
            <strong>{deleting.name}</strong> está marcada por {formatNumber(deleting.merchantCount)} loja(s) e por
            isso não pode ser excluída. Desative-a: ela sai das telas sem desmarcar ninguém, e volta
            inteira se for reativada.
          </ConfirmText>
        ) : (
          <ConfirmText>
            Tem certeza que deseja excluir <strong>{deleting?.name ?? ''}</strong>? Ela deixará de ser
            oferecida ao lojista e de aparecer nos filtros da vitrine.
          </ConfirmText>
        )}
      </Modal>
    </>
  );
};

export default AmenitiesPage;
